import { useState, useEffect, useCallback } from 'react';
import { collaboratorApi, userApi } from '../../api/client';
import './MembersManager.css';

interface Collaborator {
    id: string;
    userId: string;
    username: string;
    email: string;
    avatarUrl?: string;
    role: 'guest' | 'developer' | 'maintainer';
    createdAt: string;
}

interface SearchUser {
    id: string;
    username: string;
    email: string;
    avatarUrl?: string;
}

interface MembersManagerProps {
    repoPath: string;
    canManage: boolean;
}

const ROLE_LABELS: Record<string, { label: string; description: string }> = {
    guest: { label: '访客', description: '只读：浏览与克隆，不可推送' },
    developer: { label: '开发者', description: '可推送代码；不可管理协作者；通常不可合并他人的合并请求' },
    maintainer: {
        label: '维护者',
        description: '可推送；可管理协作者与仓库设置；可合并合并请求（与平台策略一致）',
    },
};

export function MembersManager({ repoPath, canManage }: MembersManagerProps) {
    const [members, setMembers] = useState<Collaborator[]>([]);
    const [loading, setLoading] = useState(canManage);
    const [error, setError] = useState<string | null>(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [successHint, setSuccessHint] = useState<string | null>(null);

    const loadMembers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await collaboratorApi.list(repoPath);
            setMembers(data);
        } catch (err: any) {
            setError(err.message || '加载协作者列表失败');
        } finally {
            setLoading(false);
        }
    }, [repoPath]);

    useEffect(() => {
        if (!canManage) {
            setMembers([]);
            setLoading(false);
            setError(null);
            return;
        }
        loadMembers();
    }, [canManage, loadMembers]);

    const handleRoleChange = async (userId: string, newRole: string) => {
        try {
            await collaboratorApi.update(repoPath, userId, newRole);
            setMembers(prev =>
                prev.map(m => (m.userId === userId ? { ...m, role: newRole as any } : m))
            );
        } catch (err: any) {
            alert(err.message || '更新角色失败');
        }
    };

    const handleRemove = async (userId: string, username: string) => {
        if (!confirm(`确定要移除成员 ${username} 吗？`)) return;
        try {
            await collaboratorApi.remove(repoPath, userId);
            setMembers(prev => prev.filter(m => m.userId !== userId));
        } catch (err: any) {
            alert(err.message || '移除成员失败');
        }
    };

    const handleInviteSuccess = (newMember: Collaborator) => {
        setMembers((prev) => [
            {
                ...newMember,
                createdAt: newMember.createdAt || new Date().toISOString(),
            },
            ...prev,
        ]);
        setShowInviteModal(false);
        setSuccessHint('已添加协作者');
        window.setTimeout(() => setSuccessHint(null), 3200);
    };

    if (canManage && loading) {
        return <div className="members-loading">加载中...</div>;
    }

    if (canManage && error) {
        return <div className="members-error">{error}</div>;
    }

    return (
        <div className="members-manager">
            <div className="collaborators-explainer" role="note">
                <p>
                    <strong>说明：</strong>若用户已是<strong>群组成员</strong>，其对仓库的权限仅由<strong>群组角色</strong>决定，与下表中的协作者记录<strong>不叠加</strong>。
                </p>
                <p>
                    <strong>协作者</strong>适用于<strong>未加入群组</strong>、但需要访问<strong>本仓库</strong>的用户（例如外协只读或单仓协作）。
                </p>
            </div>

            {!canManage && (
                <p className="members-no-access">
                    您无权查看或管理协作者列表。仅<strong>群组维护者、所有者</strong>或<strong>平台管理员</strong>可在此操作。
                </p>
            )}

            {successHint && <div className="members-success-hint">{successHint}</div>}

            <div className="members-header">
                <h3>仓库协作者 ({canManage ? members.length : '—'})</h3>
                {canManage && (
                    <button className="btn btn-primary" onClick={() => setShowInviteModal(true)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M8 2a.75.75 0 0 1 .75.75v4.5h4.5a.75.75 0 0 1 0 1.5h-4.5v4.5a.75.75 0 0 1-1.5 0v-4.5h-4.5a.75.75 0 0 1 0-1.5h4.5v-4.5A.75.75 0 0 1 8 2Z" />
                        </svg>
                        添加协作者
                    </button>
                )}
            </div>

            {!canManage ? null : members.length === 0 ? (
                <div className="members-empty">暂无协作者。可邀请未入群的用户仅访问本仓库。</div>
            ) : (
                <table className="members-table">
                    <thead>
                        <tr>
                            <th>用户</th>
                            <th>角色</th>
                            <th>加入时间</th>
                            {canManage && <th>操作</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {members.map(member => (
                            <tr key={member.id}>
                                <td className="member-user">
                                    <div className="member-avatar">
                                        {member.avatarUrl ? (
                                            <img src={member.avatarUrl} alt={member.username} />
                                        ) : (
                                            <span>{member.username.charAt(0).toUpperCase()}</span>
                                        )}
                                    </div>
                                    <div className="member-info">
                                        <strong>{member.username}</strong>
                                        <span>{member.email}</span>
                                    </div>
                                </td>
                                <td>
                                    {canManage ? (
                                        <select
                                            value={member.role}
                                            onChange={e => handleRoleChange(member.userId, e.target.value)}
                                            className="role-select"
                                        >
                                            <option value="guest">访客</option>
                                            <option value="developer">开发者</option>
                                            <option value="maintainer">维护者</option>
                                        </select>
                                    ) : (
                                        <span className={`role-badge role-${member.role}`}>
                                            {ROLE_LABELS[member.role]?.label || member.role}
                                        </span>
                                    )}
                                </td>
                                <td>{new Date(member.createdAt).toLocaleDateString()}</td>
                                {canManage && (
                                    <td>
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={() => handleRemove(member.userId, member.username)}
                                        >
                                            移除
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {showInviteModal && (
                <InviteMemberModal
                    repoPath={repoPath}
                    onClose={() => setShowInviteModal(false)}
                    onSuccess={handleInviteSuccess}
                />
            )}
        </div>
    );
}

interface InviteMemberModalProps {
    repoPath: string;
    onClose: () => void;
    onSuccess: (member: Collaborator) => void;
}

function InviteMemberModal({ repoPath, onClose, onSuccess }: InviteMemberModalProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
    const [selectedUser, setSelectedUser] = useState<SearchUser | null>(null);
    const [role, setRole] = useState<string>('developer');
    const [searching, setSearching] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const timer = setTimeout(async () => {
            const q = searchQuery.trim();
            // 已选定用户且输入框仍是该用户时，不再发起搜索，否则会再次填满下拉（看起来像选完又弹一层）
            if (selectedUser && q === selectedUser.username) {
                setSearchResults([]);
                setSearching(false);
                return;
            }
            if (q.length >= 2) {
                setSearching(true);
                try {
                    const results = await userApi.search(q);
                    setSearchResults(results);
                } catch {
                    setSearchResults([]);
                } finally {
                    setSearching(false);
                }
            } else {
                setSearchResults([]);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery, selectedUser]);

    const handleSelectUser = (user: SearchUser) => {
        setSelectedUser(user);
        setSearchQuery(user.username);
        setSearchResults([]);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedUser) {
            setError('请选择一个用户');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const newMember = await collaboratorApi.add(repoPath, {
                username: selectedUser.username,
                role,
            });
            onSuccess({
                ...newMember,
                avatarUrl: selectedUser.avatarUrl,
            });
        } catch (err: any) {
            setError(err.message || '邀请失败');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>添加协作者</h3>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>用户名或邮箱</label>
                        <div className="search-input-wrapper">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={e => {
                                    setSearchQuery(e.target.value);
                                    setSelectedUser(null);
                                }}
                                placeholder="搜索用户..."
                                className="form-input"
                            />
                            {searching && <div className="search-loading">搜索中...</div>}
                            {searchResults.length > 0 && (
                                <ul className="search-results">
                                    {searchResults.map(user => (
                                        <li key={user.id} onClick={() => handleSelectUser(user)}>
                                            <div className="search-result-avatar">
                                                {user.avatarUrl ? (
                                                    <img src={user.avatarUrl} alt={user.username} />
                                                ) : (
                                                    <span>{user.username.charAt(0).toUpperCase()}</span>
                                                )}
                                            </div>
                                            <div className="search-result-info">
                                                <strong>{user.username}</strong>
                                                <span>{user.email}</span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>选择角色</label>
                        <select
                            value={role}
                            onChange={e => setRole(e.target.value)}
                            className="form-select"
                        >
                            <option value="guest">访客 — 只读，不可推送</option>
                            <option value="developer">开发者 — 可推送，不可管理协作者</option>
                            <option value="maintainer">维护者 — 可管理协作者与合并请求等</option>
                        </select>
                    </div>

                    {error && <div className="form-error">{error}</div>}

                    <div className="modal-footer">
                        <button type="button" className="btn btn-ghost" onClick={onClose}>
                            取消
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={submitting || !selectedUser}
                        >
                            {submitting ? '添加中...' : '添加'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
